/**
 * Keyboard → intents. The ONLY place keyboard codes exist (US 12.01).
 * Arrows + WASD move; E / Enter / Space interact; Escape cancels;
 * I inventory; J journal.
 */
import { worldBus } from '@shared/bus.js';
import { normaliseMove, type MoveVector } from '@shared/input.js';
import type { IntentHub, IntentSource } from './intent-hub.js';

const MOVE_KEYS: Record<string, { x: number; y: number }> = {
  ArrowUp: { x: 0, y: -1 },
  ArrowDown: { x: 0, y: 1 },
  ArrowLeft: { x: -1, y: 0 },
  ArrowRight: { x: 1, y: 0 },
  KeyW: { x: 0, y: -1 },
  KeyS: { x: 0, y: 1 },
  KeyA: { x: -1, y: 0 },
  KeyD: { x: 1, y: 0 },
};

const PRESS_KEYS: Record<string, Parameters<IntentHub['press']>[0]> = {
  KeyE: 'INTERACT',
  Enter: 'CONFIRM',
  Space: 'INTERACT',
  Escape: 'CANCEL',
  KeyI: 'OPEN_INVENTORY',
  KeyJ: 'OPEN_JOURNAL',
  KeyH: 'OPEN_APARTMENT',
  KeyP: 'OPEN_ALBUM',
};

export function attachKeyboard(hub: IntentHub): IntentSource {
  const held = new Set<string>();

  const source: IntentSource & { move: MoveVector } = {
    move: { x: 0, y: 0 },
    detach() {
      window.removeEventListener('keydown', onDown);
      window.removeEventListener('keyup', onUp);
      window.removeEventListener('blur', onBlur);
    },
  };

  function recompute(): void {
    let x = 0;
    let y = 0;
    for (const code of held) {
      const d = MOVE_KEYS[code];
      if (d) {
        x += d.x;
        y += d.y;
      }
    }
    // Opposing keys cancel; result clamped/normalised (no diagonal advantage).
    source.move = normaliseMove(Math.sign(x), Math.sign(y));
  }

  function onDown(ev: KeyboardEvent): void {
    if (ev.repeat) return;
    // Touch controls auto-hide when the keyboard takes over (US 12.02).
    if (MOVE_KEYS[ev.code] || PRESS_KEYS[ev.code]) {
      worldBus.emit('input-device', { kind: 'keyboard' });
    }
    if (MOVE_KEYS[ev.code]) {
      held.add(ev.code);
      recompute();
      ev.preventDefault();
    }
    const intent = PRESS_KEYS[ev.code];
    if (intent) {
      hub.press(intent);
      ev.preventDefault();
    }
  }

  function onUp(ev: KeyboardEvent): void {
    if (held.delete(ev.code)) recompute();
  }

  /** Tab-away must not leave keys stuck held. */
  function onBlur(): void {
    held.clear();
    recompute();
  }

  window.addEventListener('keydown', onDown);
  window.addEventListener('keyup', onUp);
  window.addEventListener('blur', onBlur);
  hub.attach(source);
  return source;
}
